import type {
  Product,
  ProductSuccessor
} from '@/modules/types/product/ProductSchema.types'

/**
 * Sold-out PDPs whose successor vintage is in stock redirect there, with a
 * notice naming both vintages (VIP-12594). The Gatekeeper feature splits
 * devices into the treatment (active: redirected) and the holdout (inactive:
 * stays on the sold-out page) that proves what the redirect adds. Its
 * percentage restraint is the treatment share; 0 switches the redirect off.
 */
export const SUCCESSOR_REDIRECT_FEATURE = 'pdp_successor_redirect'

/**
 * Keeps a sold-out PDP from redirecting — the notice links back with it, so
 * a customer who wants the old vintage can still read about it.
 */
export const SUCCESSOR_REDIRECT_OPT_OUT_PARAM = 'sold_out_vintage'

/**
 * How long a sold-out page keeps its skeleton waiting for the device's group.
 * The wait comes before the group is known, so it costs both groups alike;
 * a device whose features are slower joins neither group for this visit.
 */
export const SUCCESSOR_REDIRECT_FEATURES_TIMEOUT = 1000

/** Key of the redirect's origin in the successor entry's history state */
export const SUCCESSOR_REDIRECT_STATE_KEY = 'successorRedirectFrom'

export type SuccessorRedirectVariant = 'treatment' | 'control'

interface GatekeeperFeature {
  feature_name: string
  is_active: boolean
}

/** The sold-out product the notice on the successor PDP talks about */
export interface SuccessorRedirectOrigin {
  product_id: number
  campaign_id: number | null
  name: string | null
  wine_year: string | null
  /** Path of the sold-out PDP, without the opt-out parameter */
  path: string
  /** The successor the redirect went to; the notice shows only there */
  successor_product_id: number
  successor_wine_year: string | null
}

/**
 * The device's group, or null while it is in neither: the features have not
 * loaded (or failed), or the feature does not exist. Outside both groups
 * nothing redirects and nothing is tracked.
 */
export const getSuccessorRedirectVariant = (
  features: GatekeeperFeature[] = []
): SuccessorRedirectVariant | null => {
  const feature = features.find(
    ({ feature_name: name }) => name === SUCCESSOR_REDIRECT_FEATURE
  )
  if (!feature) return null
  return feature.is_active ? 'treatment' : 'control'
}

/**
 * The successor a sold-out product page should hand over to, never the
 * product itself (a catalog loop would redirect in place).
 */
export const getRedirectSuccessor = (
  product: Pick<Product, 'product_id' | 'successor'>
): ProductSuccessor | null => {
  const { successor, product_id: productId } = product
  return successor && successor.product_id !== productId ? successor : null
}

/**
 * Elbwalker exposures already reported, per store instance: refreshProduct
 * runs again on every query or hash change, and the holdout stays on the
 * page, so without this it would count one visit several times
 */
const _exposures = new WeakMap<object, Set<string>>()

/** True the first time a product and successor pair is seen by `owner` */
export const isFirstExposure = (
  owner: object,
  productId: number,
  successorProductId: number
): boolean => {
  const seen = _exposures.get(owner) ?? new Set<string>()
  _exposures.set(owner, seen)
  const key = `${productId}>${successorProductId}`
  if (seen.has(key)) return false
  seen.add(key)
  return true
}

/** The successor's path on this site; the API answers with absolute URLs */
export const toSitePath = (url: string): string =>
  url.replace(/^https?:\/\/[^/]+/, '') || '/'

/**
 * Whether the visitor entered the shop on this page rather than navigating
 * to it. vue-router keeps the previous entry in `history.state.back`, which
 * is null exactly on the first page of a visit (and after a reload of it).
 */
export const isLandingEntry = (): boolean =>
  globalThis.history?.state?.back == null

/** The redirect's origin, if this history entry was reached through one */
export const readRedirectOrigin = (
  productId: number | null | undefined
): SuccessorRedirectOrigin | null => {
  const origin = globalThis.history?.state?.[SUCCESSOR_REDIRECT_STATE_KEY]
  if (!origin || productId == undefined) return null
  // vue-router carries the state over to later replaces of the same entry,
  // so it must still be about the product on screen
  return origin.successor_product_id === Number(productId) ? origin : null
}

/**
 * Pushes an elbwalker `successor_redirect <action>` event. Everything the
 * evaluation joins on travels in `data`, the one part of the event the
 * warehouse stores as is.
 */
export const trackSuccessorRedirect = async (
  action: 'exposure' | 'view' | 'back',
  data: Record<string, string | number | boolean | null>
): Promise<void> => {
  const { pushWalkerEvent } = await import('@/services/helpers/ElbwalkerHelper')
  pushWalkerEvent(`successor_redirect ${action}`, data)
}
